import type { TextFieldProps } from '@shopify/polaris';

import { SearchIcon, XCircleIcon } from '@shopify/polaris-icons';
import { Icon, TextField } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';

export interface GSearchFieldProps extends Pick<
  TextFieldProps,
  'disabled' | 'autoFocus' | 'size' | 'onFocus' | 'onBlur'
> {
  searchValue: string;
  isLoading: boolean;
  searchPlaceholder: string;
  onSearch: (value: string) => void;
}

export const GSearchField: React.FC<GSearchFieldProps> = ({
  onSearch,
  searchValue,
  isLoading,
  searchPlaceholder,
  ...props
}) => {
  const { t } = useTranslation();

  // Bare icon button so the tap fires on `pointerdown` (before the mobile
  // keyboard-dismiss blur), unlike TextField's built-in `clearButton`.
  const clearControl = searchValue ? (
    <button
      type="button"
      aria-label={t('Clear')}
      className="flex items-center cursor-pointer"
      // Inline reset beats the global `button` rule that adds native gray chrome.
      style={{
        appearance: 'none',
        WebkitAppearance: 'none',
        background: 'transparent',
        border: 0,
        padding: 0,
        WebkitTapHighlightColor: 'transparent',
      }}
      onPointerDown={(event) => {
        // pointerdown isn't passive (unlike React's touchstart), so preventDefault
        // blocks the blur before clear; fires once for both mouse and touch.
        event.preventDefault();
        onSearch('');
      }}
      // Keyboard (Enter/Space) only emits click — keep onClick so keyboard/SR users can clear.
      onClick={() => onSearch('')}
    >
      <Icon source={XCircleIcon} tone="subdued" />
    </button>
  ) : undefined;

  return (
    <div className="flex grow items-center gap-[8px]">
      <div className="grow">
        <TextField
          label=""
          size="slim"
          placeholder={searchPlaceholder}
          autoComplete="off"
          value={searchValue}
          onChange={onSearch}
          loading={isLoading}
          prefix={<Icon source={SearchIcon} tone="base" />}
          suffix={clearControl}
          {...props}
        />
      </div>
    </div>
  );
};
